import { createReducer } from '@reduxjs/toolkit';
import { GoodsManipulationItemDto, GoodsStatus } from '../../api/ClientApi';
import { myAssert } from '../../common/console';
import { clearBaseEntityState, IBaseEntityState, SaveEntityStatus } from './common';
import {
    createNewSellGoodsManipulationItem,
    createNewUseGoodsManipulationItem,
    createNewWriteOffGoodsManipulationItem,
    createNewWriteOffInTransportGoodsManipulationItem,
    clearGoodsManipulationItem,
    setGoodsManipulationItemArticleId,
    setGoodsManipulationItemOrderId,
    setGoodsManipulationItemExecutionTime,
    setGoodsManipulationItemSiteId,
    setGoodsManipulationItemQuantity,
    setGoodsManipulationItemReason,
    setGoodsManipulationItemSellingPrice,
    setGoodsManipulationItemClientId,
    writeOffGoods,
    writeOffGoodsForTransport,
    useGoods,
    sellGoods,
    setGoodsManipulationItemSituation,
} from '../actions/goodsActions';

export interface GoodsStatusManipulationFormReducerState extends IBaseEntityState {
    goodsManipulationItem: GoodsManipulationItemDto | null;
}

function clearState(state: GoodsStatusManipulationFormReducerState): void {
    state.goodsManipulationItem = null;

    clearBaseEntityState(state);
}

function getInitialState(): GoodsStatusManipulationFormReducerState {
    const state: GoodsStatusManipulationFormReducerState = {
        goodsManipulationItem: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

export const goodsStatusManipulationFormReducer = createReducer<GoodsStatusManipulationFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(
                createNewSellGoodsManipulationItem,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    const { siteId, articleId } = action.payload;

                    const newGoodsManipulationItem: GoodsManipulationItemDto = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.executionTime = new Date();
                    newGoodsManipulationItem.targetStatus = GoodsStatus.Sold;
                    newGoodsManipulationItem.isInTransport = false;
                    newGoodsManipulationItem.goodsOriginSiteId = siteId;
                    newGoodsManipulationItem.articleId = articleId;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                },
            )
            .addCase(
                createNewUseGoodsManipulationItem,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    const { siteId, articleId } = action.payload;

                    const newGoodsManipulationItem: GoodsManipulationItemDto = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.executionTime = new Date();
                    newGoodsManipulationItem.targetStatus = GoodsStatus.Used;
                    newGoodsManipulationItem.isInTransport = false;
                    newGoodsManipulationItem.goodsOriginSiteId = siteId;
                    newGoodsManipulationItem.articleId = articleId;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                },
            )
            .addCase(
                createNewWriteOffGoodsManipulationItem,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    const { siteId, articleId } = action.payload;

                    const newGoodsManipulationItem: GoodsManipulationItemDto = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.executionTime = new Date();
                    newGoodsManipulationItem.targetStatus = GoodsStatus.WrittenOff;
                    newGoodsManipulationItem.isInTransport = false;
                    newGoodsManipulationItem.goodsOriginSiteId = siteId;
                    newGoodsManipulationItem.articleId = articleId;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                },
            )
            .addCase(
                createNewWriteOffInTransportGoodsManipulationItem,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    const { orderId, articleId } = action.payload;

                    const newGoodsManipulationItem: GoodsManipulationItemDto = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.executionTime = new Date();
                    newGoodsManipulationItem.targetStatus = GoodsStatus.WrittenOff;
                    newGoodsManipulationItem.isInTransport = true;
                    newGoodsManipulationItem.articleId = articleId;
                    newGoodsManipulationItem.goodsOriginOrderId = orderId;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                },
            )
            .addCase(clearGoodsManipulationItem, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(
                setGoodsManipulationItemArticleId,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if it is being updated',
                    );

                    const articleId = action.payload;

                    if (!!state.goodsManipulationItem) {
                        const newGoodsManipulationItem = new GoodsManipulationItemDto();
                        newGoodsManipulationItem.init(state.goodsManipulationItem);
                        newGoodsManipulationItem.articleId = articleId;

                        state.goodsManipulationItem = newGoodsManipulationItem;
                    }
                },
            )
            .addCase(setGoodsManipulationItemOrderId, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if it is being updated');

                const orderId = action.payload;

                if (
                    !!state.goodsManipulationItem &&
                    state.goodsManipulationItem.targetStatus === GoodsStatus.WrittenOff &&
                    state.goodsManipulationItem.isInTransport
                ) {
                    const newGoodsManipulationItem = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.init(state.goodsManipulationItem);
                    newGoodsManipulationItem.goodsOriginOrderId = orderId;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                }
            })
            .addCase(
                setGoodsManipulationItemExecutionTime,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if it is being updated',
                    );

                    const executionTime = action.payload;

                    if (!!state.goodsManipulationItem) {
                        const newGoodsManipulationItem = new GoodsManipulationItemDto();
                        newGoodsManipulationItem.init(state.goodsManipulationItem);
                        newGoodsManipulationItem.executionTime = executionTime;

                        state.goodsManipulationItem = newGoodsManipulationItem;
                    }
                },
            )
            .addCase(setGoodsManipulationItemSiteId, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if it is being updated');

                const siteId = action.payload;

                if (!!state.goodsManipulationItem && !state.goodsManipulationItem.isInTransport) {
                    const newGoodsManipulationItem = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.init(state.goodsManipulationItem);
                    newGoodsManipulationItem.goodsOriginSiteId = siteId;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                }
            })
            .addCase(
                setGoodsManipulationItemQuantity,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if it is being updated',
                    );

                    const quantity = action.payload;

                    if (!!state.goodsManipulationItem) {
                        const newGoodsManipulationItem = new GoodsManipulationItemDto();
                        newGoodsManipulationItem.init(state.goodsManipulationItem);
                        newGoodsManipulationItem.quantity = quantity;

                        state.goodsManipulationItem = newGoodsManipulationItem;
                    }
                },
            )
            .addCase(
                setGoodsManipulationItemSituation,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if it is being updated',
                    );

                    const usedOnSituationId = action.payload;

                    if (!!state.goodsManipulationItem) {
                        const newGoodsManipulationItem = new GoodsManipulationItemDto();
                        newGoodsManipulationItem.init(state.goodsManipulationItem);
                        newGoodsManipulationItem.usedOnSituationId = usedOnSituationId;

                        state.goodsManipulationItem = newGoodsManipulationItem;
                    }
                },
            )
            .addCase(setGoodsManipulationItemReason, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if it is being updated');

                const reason = action.payload;

                if (!!state.goodsManipulationItem) {
                    const newGoodsManipulationItem = new GoodsManipulationItemDto();
                    newGoodsManipulationItem.init(state.goodsManipulationItem);
                    newGoodsManipulationItem.reason = reason;

                    state.goodsManipulationItem = newGoodsManipulationItem;
                }
            })
            .addCase(
                setGoodsManipulationItemSellingPrice,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if it is being updated',
                    );

                    const sellingPrice = action.payload;

                    if (
                        !!state.goodsManipulationItem &&
                        state.goodsManipulationItem.targetStatus === GoodsStatus.Sold
                    ) {
                        const newGoodsManipulationItem = new GoodsManipulationItemDto();
                        newGoodsManipulationItem.init(state.goodsManipulationItem);
                        newGoodsManipulationItem.sellingPrice = sellingPrice;

                        state.goodsManipulationItem = newGoodsManipulationItem;
                    }
                },
            )
            .addCase(
                setGoodsManipulationItemClientId,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if it is being updated',
                    );

                    const clientId = action.payload;

                    if (
                        !!state.goodsManipulationItem &&
                        state.goodsManipulationItem.targetStatus === GoodsStatus.Sold
                    ) {
                        const newGoodsManipulationItem = new GoodsManipulationItemDto();
                        newGoodsManipulationItem.init(state.goodsManipulationItem);
                        newGoodsManipulationItem.soldToClientId = clientId;

                        state.goodsManipulationItem = newGoodsManipulationItem;
                    }
                },
            )
            .addCase(writeOffGoods.pending, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(writeOffGoods.fulfilled, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(writeOffGoods.rejected, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(
                writeOffGoodsForTransport.pending,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if save is on its way',
                    );
                    myAssert(
                        () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                        '',
                        false,
                    );

                    state.saveStatus = SaveEntityStatus.Pending;
                },
            )
            .addCase(
                writeOffGoodsForTransport.fulfilled,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if save is on its way',
                    );
                    myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                    state.saveStatus = SaveEntityStatus.Saved;
                },
            )
            .addCase(
                writeOffGoodsForTransport.rejected,
                (state: GoodsStatusManipulationFormReducerState, action: any) => {
                    myAssert(
                        () => state.goodsManipulationItem !== null,
                        'There needs to be goods if save is on its way',
                    );
                    myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                    state.saveStatus = SaveEntityStatus.Error;
                },
            )
            .addCase(useGoods.pending, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(useGoods.fulfilled, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(useGoods.rejected, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(sellGoods.pending, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(sellGoods.fulfilled, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(sellGoods.rejected, (state: GoodsStatusManipulationFormReducerState, action: any) => {
                myAssert(() => state.goodsManipulationItem !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
